'use client';

import { useAddNativeClasses } from '@carvana/merch-native/hooks';
import { useContext } from 'react';
import useDisplayTradeAppliedToast from '@/common/TradeAppliedToastContent/useDisplayTradeAppliedToast';
import { heliosSizes } from '~/constants/common/resizeBreakpoints';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import { useNativeDeviceType } from '~/context/common/DeviceContext';
import TradeContext from '~/context/common/TradeContext';
import { useIsLoggedIn } from '~/context/common/UserContext';
import ModalContext from '~/context/srp/ModalContext';
import useToggleAccountModal from '~/hooks/common/accountModal/useToggleAccountModal';
import useGetFavorites from '~/hooks/common/favorites/useGetFavorites';
import useGeolocation from '~/hooks/common/geolocation/useGeolocation';
import useGetSavedSearches from '~/hooks/common/services/useGetSavedSearches';
import useGetCommonTrades from '~/hooks/common/services/useGetTrades';
import useSyncGeolocation from '~/hooks/common/sync/useSyncGeolocation';
import useSynchronizeAuth from '~/hooks/common/sync/useSynchronizeAuth';
import useToggleAppliedTradeCookie from '~/hooks/common/sync/useToggleAppliedTradeCookie';
import useDataDogRUM from '~/hooks/common/telemetry/useDataDogRUM';
import useSetTradeIncentiveDDContext from '~/hooks/common/telemetry/useSetTradeIncentiveDDContext';
import useTrackBrowserCookieIdMismatch from '~/hooks/common/telemetry/useTrackBrowserCookieIdMismatch';
import { useTrackClickEvents } from '~/hooks/common/telemetry/useTrackClickEvents';
import useTrackServerClientMobileMismatch from '~/hooks/common/telemetry/useTrackServerClientMobileMismatch';
import useTrackUserGeolocations from '~/hooks/common/telemetry/useTrackUserGeolocations';
import useResizeListener from '~/hooks/common/ui/useResizeListener';
import useSetScrollbarWidthProp from '~/hooks/common/ui/useSetScrollbarWidthProp';
import useSebastianLoaded from '~/hooks/common/useSebastianLoaded';
import useNavigation from '~/hooks/srp/appRouter/useNavigation';
import useSyncUrl from '~/hooks/srp/appRouter/useSyncUrl';
import { useTrackResults } from '~/hooks/srp/events';
import { useGetLowInventoryAvailability } from '~/hooks/srp/services/useGetAvailability';
import useGetAvailability from '~/hooks/srp/services/useGetAvailability';
import useGetInventory, {
  useGetFacetData,
  useGetSuggestedFilters
} from '~/hooks/srp/services/useGetInventory';
import useGetPricing from '~/hooks/srp/services/useGetPricing';
import useAddMetaAttributes from '~/hooks/srp/useAddMetaAttributes';
import useAlertHighNetTradeIssues from '~/hooks/srp/useAlertHighNetTradeIssues';
import useClearFreeTextNoResultsOnFilterChange from '~/hooks/srp/useClearFreeTextNoResultsOnFilterChange';
import useComposerEvents from '~/hooks/srp/useComposerEvents';
import useEnsureFinanceFiltersAreInRange from '~/hooks/srp/useEnsureFinanceFiltersAreInRange';
import useEnsureValidSortBy from '~/hooks/srp/useEnsureValidSortBy';
import useGetCreditStatus from '~/hooks/srp/useGetCreditStatus';
import useLogHasNewVehicleFilter from '~/hooks/srp/useLogHasNewVehicleFilter';
import useLogMissingConditionalTiles from '~/hooks/srp/useLogMissingConditionalTiles';
import useLogNewCarConfiguratorTileReceived from '~/hooks/srp/useLogNewCarConfiguratorTileReceived';
import useLogZipCodeChanges from '~/hooks/srp/useLogZipCodeChanges';
import useMerchWindowObject from '~/hooks/srp/useMerchWindowObject';
import useModalScrollRestoration from '~/hooks/srp/useModalScrollRestoration';
import useQualiModal from '~/hooks/srp/useQualiModal';
import useRegenerateSearchSessionId from '~/hooks/srp/useRegenerateSearchSessionId';
import useResetSortOnNewSearch from '~/hooks/srp/useResetSortOnNewSearch';
import useSearchAnalyzeEventListener from '~/hooks/srp/useSearchAnalyzeEventListener';
import useSendSrpSebastianPageContext from '~/hooks/srp/useSendSrpSebastianPageContext';
import useSendTradeIncentiveEvents from '~/hooks/srp/useSendTradeIncentiveEvents';
import useSetDataDogContext from '~/hooks/srp/useSetDataDogContext';
import useSetPageNumber from '~/hooks/srp/useSetPageNumber';
import useShowFinance from '~/hooks/srp/useShowFinance';
import useShowManageSavedSearchesOnLoad from '~/hooks/srp/useShowManageSavedSearchesOnLoad';
import useTrackBackForwardCache from '~/hooks/srp/useTrackBackForwardCache';
import useTrackSuggestedFilters from '~/hooks/srp/useTrackSuggestedFilters';
import useTrackTilePerformance from '~/hooks/srp/useTrackTilePerformance';
import useTradeToggled from '~/hooks/srp/useTradeToggled';
import useTurboBoostRumContext from '~/hooks/srp/useTurboBoostRumContext';
import {
  useNativeMessageDispatcher,
  useNativeMessageListener
} from '~/native/srp';
import { computeServerInventoryIsGood } from '~/utilities/routerCacheStaleness';
import inventoryCache, { getSrpCacheKey } from '~/utilities/srp/inventoryCache';

type OwnProps = {
  fetchedFacetDataOnServer: boolean;
  fetchedInventoryOnServer: boolean;
  freeTextNoResults: boolean;
};

export default function SearchData(props: OwnProps) {
  const { isAccountModalOpen, setAccountModalOpen } = useContext(ModalContext);
  const { preferredAcquisitionNameFromQueryParam } = useContext(TradeContext);
  const { hasStaleRouterCache } = useContext(AppliedFiltersContext);
  const serverInventoryIsGood = computeServerInventoryIsGood({
    fetchedInventoryOnServer: props.fetchedInventoryOnServer,
    freeTextNoResults: props.freeTextNoResults,
    hasStaleRouterCache
  });
  // On a stale/skeleton RSC, a cache hit restores inventory instead of forcing
  // a fetch — mirrors the seed gate in InventoryContextProvider.
  const restoreFromCache =
    !serverInventoryIsGood && inventoryCache.has(getSrpCacheKey() ?? '');
  const inventorySatisfiedOnServer = serverInventoryIsGood || restoreFromCache;
  const isLoggedIn = useIsLoggedIn();
  const nativeDeviceType = useNativeDeviceType();

  useAddMetaAttributes();
  useClearFreeTextNoResultsOnFilterChange();
  useComposerEvents();
  useDataDogRUM();
  useSynchronizeAuth();
  useSyncUrl();
  useShowManageSavedSearchesOnLoad();
  useGetCreditStatus();
  useGetCommonTrades(isLoggedIn, preferredAcquisitionNameFromQueryParam);
  useTradeToggled();
  useDisplayTradeAppliedToast();
  useResizeListener(heliosSizes);
  useSetScrollbarWidthProp();
  useGetFavorites();
  useGetFacetData(props.fetchedFacetDataOnServer);
  useGetInventory(inventorySatisfiedOnServer);
  useGetSuggestedFilters();
  useTrackSuggestedFilters();
  useGetAvailability();
  useGetPricing();
  useGetSavedSearches();
  useNavigation();
  useSetPageNumber();
  useShowFinance();
  useToggleAppliedTradeCookie();
  useTrackClickEvents();
  useTrackResults();
  useTrackTilePerformance();
  useSetDataDogContext();
  useTurboBoostRumContext();
  useGetLowInventoryAvailability();
  useLogZipCodeChanges();
  useNativeMessageDispatcher();
  useNativeMessageListener();
  useAddNativeClasses(nativeDeviceType.isNative, nativeDeviceType.platform);
  useModalScrollRestoration();
  useMerchWindowObject();
  useRegenerateSearchSessionId();
  useResetSortOnNewSearch();
  useEnsureFinanceFiltersAreInRange();
  useEnsureValidSortBy();
  useTrackBackForwardCache();
  useTrackBrowserCookieIdMismatch();
  useTrackServerClientMobileMismatch();
  useSearchAnalyzeEventListener();
  useSendSrpSebastianPageContext();
  useSendTradeIncentiveEvents();
  useSetTradeIncentiveDDContext();
  useLogHasNewVehicleFilter();
  useLogMissingConditionalTiles();
  useLogNewCarConfiguratorTileReceived();
  useGeolocation();
  useSyncGeolocation();
  useToggleAccountModal({ isAccountModalOpen, setAccountModalOpen });
  useAlertHighNetTradeIssues();
  useTrackUserGeolocations();
  useQualiModal();
  useSebastianLoaded();

  return null;
}
